import { Suspense } from "react";
import { useTranslations } from "use-intl";
import { toast } from "sonner";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  RelocationLineEmailRecord,
  relocationLineEmailQuery,
} from "@/app/Relocations/GraphQL/relocationLineEmailQuery";
import { resolveRelocationLineEmailMutation } from "@/app/Relocations/GraphQL/relocationLineEmailMutations";
import { markRelocationLineAsSoldMutation } from "@/app/Relocations/GraphQL/relocationLineReferenceMutations";
import {
  Competitor,
  RelocationLineEmailActionOutcome,
  RelocationLineEmailStatus,
} from "@/gql/graphql";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { Text } from "@/components/catalyst/text";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { formatDateTime } from "@/lib/Formatters/formatDateTime";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckIcon } from "@heroicons/react/20/solid";

const outcomeColor: {
  [key in RelocationLineEmailActionOutcome]: BadgeColor;
} = {
  [RelocationLineEmailActionOutcome.Applied]: "green",
  [RelocationLineEmailActionOutcome.AlreadySet]: "green",
  [RelocationLineEmailActionOutcome.SoldByImoova]: "zinc",
  [RelocationLineEmailActionOutcome.LineNotFound]: "red",
  [RelocationLineEmailActionOutcome.LineAmbiguous]: "yellow",
  [RelocationLineEmailActionOutcome.LineHasBooking]: "red",
  [RelocationLineEmailActionOutcome.UnknownSeller]: "yellow",
  [RelocationLineEmailActionOutcome.Uncertain]: "yellow",
};

type LineAction = RelocationLineEmailRecord["actions"][number];

export function RelocationLineEmailReviewDialog({
  emailId,
}: {
  emailId: string;
}) {
  return (
    <Suspense fallback={<div className="p-4 text-center">…</div>}>
      <ReviewContent emailId={emailId} />
    </Suspense>
  );
}

function ReviewContent({ emailId }: { emailId: string }) {
  const t = useTranslations("task.lineEmailDialog");
  const { close } = useDialogState();
  const { data } = useSuspenseGqlQuery(relocationLineEmailQuery, {
    id: emailId,
  });
  const resolve = useGqlMutation(resolveRelocationLineEmailMutation);

  const email = data.relocationLineEmail;
  const isOpen =
    email.status === RelocationLineEmailStatus.AwaitingReview ||
    email.status === RelocationLineEmailStatus.Failed;

  return (
    <BaseDialog title={t("title")} subtitle={email.email_subject} size="3xl">
      <div className="space-y-6">
        <dl className="grid grid-cols-2 gap-4 rounded-md bg-gray-50 p-4 text-sm">
          <div>
            <dt className="text-gray-500">{t("from")}</dt>
            <dd>{email.email_from}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("received")}</dt>
            <dd>{formatDateTime(email.email_received_at)}</dd>
          </div>
        </dl>

        {email.failure_reason ? (
          <WarningBanner>
            {t("failure", { reason: email.failure_reason })}
          </WarningBanner>
        ) : null}

        <div>
          <Text className="mb-1 text-sm font-medium">{t("message")}</Text>
          {email.email_text ? (
            <p className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded-md border border-zinc-200 p-3 text-sm text-gray-700">
              {email.email_text}
            </p>
          ) : (
            <Text className="text-sm text-gray-500">{t("subjectOnly")}</Text>
          )}
        </div>

        {email.actions.length > 0 ? (
          <ActionTable actions={email.actions} />
        ) : (
          <Text className="text-sm text-gray-500">{t("noActions")}</Text>
        )}

        {isOpen ? (
          <div className="flex justify-end border-t border-zinc-200 pt-4">
            <Button
              color="green"
              LeadingIcon={CheckIcon}
              isLoading={resolve.isPending}
              onClick={async () => {
                await resolve.mutateAsync({ id: email.id });
                toast.success(t("resolved"));
                await close();
              }}
            >
              {t("resolve")}
            </Button>
          </div>
        ) : null}
      </div>
    </BaseDialog>
  );
}

function ActionTable({ actions }: { actions: LineAction[] }) {
  const t = useTranslations("task.lineEmailDialog");

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("thLine")}</TableHead>
          <TableHead>{t("thChange")}</TableHead>
          <TableHead>{t("thSoldBy")}</TableHead>
          <TableHead>{t("thResult")}</TableHead>
          <TableHead>{t("thAction")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {actions.map((action, index) => (
          <TableRow key={`${action.line_reference}-${index}`}>
            <TableCell className="font-medium">
              {action.line_reference} {action.region ?? ""}
            </TableCell>
            <TableCell>{t(`lineActionType.${action.type}`)}</TableCell>
            <TableCell>{action.sold_by ?? "—"}</TableCell>
            <TableCell>
              {action.outcome ? (
                <Badge color={outcomeColor[action.outcome]}>
                  {t(`lineActionOutcome.${action.outcome}`)}
                </Badge>
              ) : null}
            </TableCell>
            <TableCell>
              {action.relocation_line_id ? (
                <LineFixMenu
                  lineId={action.relocation_line_id}
                  lineReference={action.line_reference}
                />
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function LineFixMenu({
  lineId,
  lineReference,
}: {
  lineId: string;
  lineReference: string;
}) {
  const t = useTranslations("task.lineEmailDialog");
  const { mutateAsync, isPending } = useGqlMutation(
    markRelocationLineAsSoldMutation,
  );

  async function update(sold: boolean, soldBy?: Competitor) {
    await mutateAsync({ id: lineId, sold, soldBy });
    toast.success(t("lineUpdated", { reference: lineReference }));
  }

  return (
    <Dropdown>
      <DropdownButton as={Button} plain isLoading={isPending}>
        {t("markSold")}
      </DropdownButton>
      <DropdownMenu>
        <DropdownItem onClick={() => update(true, Competitor.Transfercar)}>
          {t("soldByTransfercar")}
        </DropdownItem>
        <DropdownItem onClick={() => update(true, Competitor.Coseats)}>
          {t("soldByCoseats")}
        </DropdownItem>
        <DropdownItem onClick={() => update(true, Competitor.Drivenow)}>
          {t("soldByDrivenow")}
        </DropdownItem>
        <DropdownItem onClick={() => update(true, Competitor.Supplier)}>
          {t("withdrawn")}
        </DropdownItem>
        <DropdownItem onClick={() => update(false)}>
          {t("release")}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
